import firebase from 'firebase/app';

import CollectionName from '../../../../utils/collections';
import { SalesStage } from '../../../Model/Lead';
import { ClientStatus } from '../../../utils/statusFilter';
import { DEFAULT_AUDIENCE_TYPE, PREVIEW_RECIPIENT_LIMIT, isClientAudience } from './constants';
import {
  hasUsableFirstName,
  isClientInCountries,
  isTextableClient,
  isTextableLead,
} from './audienceEligibility';
import { loadAudienceConfig } from './loadAudienceConfig';

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

const buildStageQuery = (coachId, filter, salesStage) => {
  const query = firebase
    .firestore()
    .collection(CollectionName.LEADS)
    .where('coach', '==', coachId)
    .where('salesStage', '==', salesStage)
    .where('countryCode', 'in', filter.countries)
    .where('isDuplicate', '==', false)
    .where('createdAt', '>=', toJsDate(filter.startDate))
    .where('createdAt', '<=', toJsDate(filter.endDate));

  const scoped = filter.requireConsent
    ? query.where('isTextBlastConsentChecked', '==', true)
    : query;

  return scoped.limit(PREVIEW_RECIPIENT_LIMIT);
};

const countLeadRecipients = async (coachId, filter, requireFirstName) => {
  const { salesStages: stages } = filter;

  const needsCoachFit = stages.includes(SalesStage.UNQUALIFIED);

  const [coachData, snapshots] = await Promise.all([
    needsCoachFit ? loadAudienceConfig(coachId) : {},
    Promise.all(stages.map((salesStage) => buildStageQuery(coachId, filter, salesStage).get())),
  ]);

  const leads = snapshots.flatMap((snapshot) => snapshot.docs.map((doc) => doc.data()));
  const count = leads.filter((lead) => (
    (!requireFirstName || hasUsableFirstName(lead.firstName))
    && isTextableLead(lead, coachData)
  )).length;

  return {
    count,
    capped: snapshots.some((snapshot) => snapshot.size === PREVIEW_RECIPIENT_LIMIT),
  };
};

const countClientRecipients = async (coachId, filter, requireFirstName) => {
  const snapshot = await firebase
    .firestore()
    .collection(CollectionName.USER)
    .where('assignedCoach', '==', coachId)
    .where('flags.subscriptionStatus', '==', ClientStatus.CANCELED)
    .where('canceledAt', '>=', toJsDate(filter.startDate))
    .where('canceledAt', '<=', toJsDate(filter.endDate))
    .limit(PREVIEW_RECIPIENT_LIMIT)
    .get();

  const count = snapshot.docs.map((doc) => doc.data()).filter((user) => (
    (!requireFirstName || hasUsableFirstName(user.firstName))
    && isTextableClient(user)
    && isClientInCountries(user.phoneNumber, filter.countries)
  )).length;

  return { count, capped: snapshot.size === PREVIEW_RECIPIENT_LIMIT };
};

export const countRecipients = async (coachId, filter, requireFirstName = false) => {
  if (!coachId || !filter?.countries?.length) {
    return { count: 0, capped: false };
  }

  const audienceType = filter.audienceType || DEFAULT_AUDIENCE_TYPE;

  if (isClientAudience(audienceType)) {
    return countClientRecipients(coachId, filter, requireFirstName);
  }

  if (!filter.salesStages?.length) {
    return { count: 0, capped: false };
  }

  return countLeadRecipients(coachId, filter, requireFirstName);
};
